import type {LoaderFunctionArgs} from 'react-router'

import {loadBuildsData} from '../../../actions/builds'
import type {AppThunk} from '../../../actions/types'
import {getBuildTypeArg} from '../../../rest/buildTypes'
import {getTabsArg} from '../../../rest/tabs'
import type {LoadedEndpoints} from '../../../routes/loadEndpoint'
import {loadEndpoint} from '../../../routes/loadEndpoint'
import {getBuildsInited} from '../../../selectors'
import {restApi} from '../../../services/rest'
import {BuildTypePageTabNamesEnum, defaultBuildTypePageTabName, toBuildTypeId} from '../../../types'
import {parseBranch} from '../../../utils/branchNames'
import {queryToObject} from '../../../utils/queryParams'
import {getTabParamsKey} from '../../../utils/tabs'
import {preloadBuildTypeInvestigationsBadge} from '../../common/Badges/BuildTypeInvestigationsBadge/BuildTypeInvestigationsBadge.preload'
import {preloadSkipAssignInvestigation} from '../../common/Investigations/Investigations.preload'
import {submitPager} from '../../common/Pager/Pager.actions.base'
import {getPager} from '../../common/Pager/Pager.selectors'
import {PagerGroup} from '../../common/Pager/Pager.types'
import {preloadPermalinks} from '../../common/Permalinks/Permalinks.preload'

import {mapStateToBuildTypeHistoryProps} from './BuildTypeOverviewTab/BuildTypeHistory/BuildTypeHistory.selectors'
import {
  defaultMode,
  Modes as OverviewBuildTypeModes,
} from './BuildTypeOverviewTab/BuildTypeOverviewTab.modes'

export const preloadBuildTypePage =
  ({params, request}: LoaderFunctionArgs): AppThunk<LoadedEndpoints> =>
  (dispatch, getState) => {
    const url = new URL(request.url)
    const {
      buildTypeTab = defaultBuildTypePageTabName,
      mode = defaultMode,
      branch,
      state: buildState,
      tag,
      'agent-name-pattern': agentPattern,
      page = '1',
    } = queryToObject(url.search)
    const state = getState()

    const pageSize = 50

    if (getPager(state, PagerGroup.BUILD).pageSize !== pageSize) {
      dispatch(
        submitPager({
          pageSize,
        }),
      )
    }

    const buildTypeId = toBuildTypeId(params.buildTypeId ?? '')
    const parsedBranch = parseBranch(branch)

    const preloadedEndpoints: LoadedEndpoints = [
      ...dispatch(preloadPermalinks(buildTypeId, parsedBranch)),
      dispatch(
        loadEndpoint(restApi.endpoints.getBuildTypeNormalized, getBuildTypeArg(buildTypeId)),
      ),
      ...dispatch(preloadSkipAssignInvestigation(buildTypeId)),
    ]

    const tabParamsKey = getTabParamsKey({buildTypeId, branch: parsedBranch})
    preloadedEndpoints.push(
      dispatch(loadEndpoint(restApi.endpoints.getTabs, getTabsArg(tabParamsKey))),
    )

    if (
      buildTypeTab === BuildTypePageTabNamesEnum.OVERVIEW &&
      mode === OverviewBuildTypeModes.BUILDS
    ) {
      const {locator} = mapStateToBuildTypeHistoryProps(getState(), {
        buildTypeId,
        branch: parsedBranch,
        withCollapsedQueued: true,
        withRunningAndQueued: true,
        buildState: buildState ?? undefined,
        tag,
        agentPattern,
      })
      if (locator != null) {
        preloadedEndpoints.push(
          ...dispatch(
            loadBuildsData({
              isPreload: true,
              locator,
              withPager: true,
              page: Number(page),
              requestOptions: {
                withBuildTypeDetails: true,
                withSnapshotDependencies: false,
                withQueuedInfo: true,
                withBuildDetails: true,
                essential: !getBuildsInited(state, locator),
              },
              extraRequestOptions: {
                withShortBuildDetails: true,
                withRunningInfo: true,
              },
            }),
          ),
        )
      }
    }
    preloadedEndpoints.push(...dispatch(preloadBuildTypeInvestigationsBadge(buildTypeId)))

    return preloadedEndpoints
  }
